import intersect from '@alpinejs/intersect';
import ajax from '@imacrayon/alpine-ajax';
import masonry from 'alpinejs-masonry';

import { Alpine } from '@webikon/webentor-core/_alpine';

/**
 * Safe arithmetic evaluator for the configurator's per-tab `formula`.
 * Supports + - * / ( ) and the single variable `value`. No eval/Function —
 * a tiny recursive-descent parser, so author-supplied formulas can't run code.
 */
function safeFormula(expr: string, value: number): number {
  const tokens = (expr || 'value').match(/\d+\.?\d*|value|[+\-*/()]/g) || [];
  let pos = 0;
  const peek = () => tokens[pos];
  const next = () => tokens[pos++];

  const parseExpr = (): number => {
    let left = parseTerm();
    while (peek() === '+' || peek() === '-') {
      const op = next();
      const right = parseTerm();
      left = op === '+' ? left + right : left - right;
    }
    return left;
  };
  const parseTerm = (): number => {
    let left = parseFactor();
    while (peek() === '*' || peek() === '/') {
      const op = next();
      const right = parseFactor();
      left = op === '*' ? left * right : right === 0 ? 0 : left / right;
    }
    return left;
  };
  const parseFactor = (): number => {
    const t = peek();
    if (t === '(') {
      next();
      const v = parseExpr();
      if (peek() === ')') next();
      return v;
    }
    if (t === 'value') {
      next();
      return value;
    }
    next();
    const n = parseFloat(t);
    return isNaN(n) ? 0 : n;
  };

  const result = parseExpr();
  return isFinite(result) ? result : 0;
}

// Extend Alpine with more components, plugins, etc.
document.addEventListener('alpine:init', () => {
  Alpine.data('menu', function () {
    return {
      __type: 'menu',
      open: false,
      isTouch: false,
      init() {},
      openPopover(id: string) {
        this.open = id;
      },
      closePopover() {
        this.open = false;
      },
      toggle(id: string) {
        if (this.open === id) {
          this.open = false;
        } else {
          this.open = id;
        }
      },
    };
  });

  // Hero main navigation: owns the mobile/tablet in-panel menu open-state.
  Alpine.data('heroNav', function () {
    return {
      __type: 'heroNav',
      navOpen: false,
      init() {
        // The desktop bar shows from xl up; the in-panel menu is xl:hidden and
        // the hero content fades out while navOpen. If the viewport grows to xl
        // while the menu is open, close it so the panel never ends up empty.
        if (typeof window !== 'undefined' && window.matchMedia) {
          const xl = window.matchMedia('(min-width: 1280px)');
          const sync = () => {
            if (xl.matches) {
              this.navOpen = false;
            }
          };
          xl.addEventListener('change', sync);
          sync();
        }
      },
    };
  });

  Alpine.data('statCounter', function () {
    return {
      __type: 'statCounter',
      display: '' as string,
      target: 0,
      prefix: '' as string,
      suffix: '' as string,
      useThousands: false,
      decimals: 0,
      hasRun: false,
      entered: true,
      duration: 2000,
      staggerStep: 200,
      init() {
        // Parse the figure span only — the x-data root also contains the label.
        const figureEl = this.$refs.figure ?? this.$el;
        const raw = (figureEl.textContent || '').trim();
        const match = raw.match(/^([^0-9-]*)([\d.,\s-]+)(.*)$/);
        if (match) {
          this.prefix = match[1] || '';
          const numericPart = match[2] || '';
          const numericRaw = numericPart.trim();
          // Whitespace around the number belongs to prefix/suffix ("3,1 mld.").
          this.prefix += numericPart.match(/^\s*/)?.[0] ?? '';
          this.suffix =
            (numericPart.match(/\s*$/)?.[0] ?? '') + (match[3] || '');
          if (/^\d{1,3},\d{1,2}$/.test(numericRaw)) {
            // Slovak decimal comma ("1,6" / "3,1"), not a thousands separator.
            this.decimals = numericRaw.split(',')[1].length;
            this.target = parseFloat(numericRaw.replace(',', '.'));
          } else {
            this.useThousands = /,/.test(numericRaw);
            const cleaned = numericRaw.replace(/[\s,]/g, '');
            const parsed = parseFloat(cleaned);
            this.target = isNaN(parsed) ? 0 : parsed;
          }
        } else {
          this.prefix = '';
          this.suffix = '';
          this.target = 0;
        }
        this.display = raw;
        this.entered = false;
      },
      reduceMotion() {
        return (
          typeof window !== 'undefined' &&
          window.matchMedia &&
          window.matchMedia('(prefers-reduced-motion: reduce)').matches
        );
      },
      // Sibling position among the stacked counters drives the stagger delay.
      staggerIndex() {
        const parent = this.$el.parentElement;
        if (!parent) return 0;
        const siblings = Array.from(parent.children).filter((el) =>
          el.classList.contains('b-stat-counter'),
        );
        return Math.max(siblings.indexOf(this.$el), 0);
      },
      enter() {
        if (this.hasRun) return;
        this.hasRun = true;

        if (this.reduceMotion()) {
          this.entered = true;
          this.display = this.format(this.target);
          return;
        }

        setTimeout(() => {
          this.entered = true;
          this.animate();
        }, this.staggerIndex() * this.staggerStep);
      },
      format(value: number) {
        if (this.decimals > 0) {
          return `${this.prefix}${value.toFixed(this.decimals).replace('.', ',')}${this.suffix}`;
        }
        const rounded = Math.round(value);
        const formatted = this.useThousands
          ? rounded.toLocaleString('en-US')
          : String(rounded);
        return `${this.prefix}${formatted}${this.suffix}`;
      },
      animate() {
        if (this.target === 0) {
          this.display = this.format(this.target);
          return;
        }

        const startTime = performance.now();
        const duration = this.duration;
        const target = this.target;

        const step = (now: number) => {
          const elapsed = now - startTime;
          const progress = Math.min(elapsed / duration, 1);
          // easeOutCubic
          const eased = 1 - Math.pow(1 - progress, 3);
          const current = target * eased;
          this.display = this.format(current);
          if (progress < 1) {
            requestAnimationFrame(step);
          } else {
            this.display = this.format(target);
          }
        };

        requestAnimationFrame(step);
      },
    };
  });

  Alpine.data('configurator', function () {
    return {
      __type: 'configurator',
      tabs: [] as Record<string, unknown>[],
      activeIndex: 0,
      value: 0,
      get active() {
        return (
          this.tabs[this.activeIndex] || {
            name: '',
            unit: '',
            min: 0,
            max: 100,
            step: 1,
            default: 0,
            formula: 'value',
          }
        );
      },
      init() {
        try {
          this.tabs = JSON.parse(this.$el.dataset.tabs || '[]');
        } catch {
          this.tabs = [];
        }
        this.value = Number(this.active.default ?? this.active.min ?? 0);
      },
      setTab(i: number) {
        this.activeIndex = i;
        this.value = Number(this.active.default ?? this.active.min ?? 0);
      },
      get fillPct() {
        const min = Number(this.active.min);
        const max = Number(this.active.max);
        const range = max - min || 1;
        return Math.min(100, Math.max(0, ((this.value - min) / range) * 100));
      },
      get display() {
        const out = safeFormula(this.active.formula, Number(this.value));
        const unit = this.active.unit ? ` ${this.active.unit}` : '';
        return `${Math.round(out)}${unit}`;
      },
      /**
       * Resolve the active tab's range rules against the current slider value
       * and open the matching floorplan modal. Rules are half-open intervals
       * [from, to); `to` null/empty means open-ended ("nad X"). Falls back to
       * the block-level floorplanId (data-fallback-id), then to the CTA URL.
       */
      submit() {
        const rules = (this.active.rules || []) as {
          from: number | string;
          to: number | string | null;
          floorplanId: number | string;
        }[];
        const v = Number(this.value);
        const hit = rules.find(
          (r) =>
            v >= Number(r.from) &&
            (r.to === null || r.to === '' || v < Number(r.to)),
        );
        const id = Number(hit?.floorplanId || this.$el.dataset.fallbackId || 0);
        if (id > 0) {
          this.$store.floorplan.open(String(id), { source: 'calculator' });
          return;
        }
        const url = this.$el.dataset.ctaUrl;
        if (url) window.location.assign(url);
      },
    };
  });

  Alpine.store('floorplan', {
    activeId: null as string | null,
    // Where the modal was opened from. 'calculator' gates the floorplan
    // download behind the HubSpot form; anything else keeps a direct download.
    source: null as string | null,
    lastFocus: null as HTMLElement | null,
    open(id: string | number, opts: { source?: string } = {}) {
      this.lastFocus = document.activeElement as HTMLElement;
      this.activeId = String(id);
      // Reset on EVERY open so a later map-open clears a stale 'calculator'.
      this.source = opts.source ?? null;
      document.documentElement.style.overflow = 'hidden';
    },
    close() {
      this.activeId = null;
      this.source = null;
      document.documentElement.style.overflow = '';
      if (this.lastFocus && typeof this.lastFocus.focus === 'function') {
        this.lastFocus.focus();
      }
    },
  });

  /**
   * Second-level modal stacked ABOVE the floorplan modal. Holds a HubSpot form
   * that must be submitted before the floorplan file downloads (calculator path
   * only). The floorplan modal stays open behind it; closing here returns there.
   */
  Alpine.store('floorplanForm', {
    isOpen: false,
    step: 'form' as 'form' | 'done',
    pendingUrl: null as string | null,
    formId: null as string | null,
    lastFocus: null as HTMLElement | null,
    open(url: string) {
      this.lastFocus = document.activeElement as HTMLElement;
      this.pendingUrl = url;
      this.step = 'form';
      this.isOpen = true;
      // Floorplan modal already set overflow:hidden; keep it.
      document.documentElement.style.overflow = 'hidden';
    },
    close() {
      this.isOpen = false;
      this.step = 'form';
      // The floorplan modal stays open behind — only release the scroll lock if
      // it has since closed (defensive; normally it's still open).
      if (
        (Alpine.store('floorplan') as { activeId: string | null }).activeId ===
        null
      ) {
        document.documentElement.style.overflow = '';
      }
      if (this.lastFocus && typeof this.lastFocus.focus === 'function') {
        this.lastFocus.focus();
      }
    },
    reset() {
      this.step = 'form';
      this.pendingUrl = null;
    },
  });

  /**
   * Parking-inquiry modal (front page only; partial rendered by app/custom.php).
   * Standalone — not stacked on the floorplan modal — so it owns its scroll lock
   * outright. Pure open/close: HubSpot handles submit + its own inline thank-you,
   * so there is no form-instance JS (no getForms/prefill/submit-detection).
   */
  Alpine.store('parkingForm', {
    isOpen: false,
    lastFocus: null as HTMLElement | null,
    open() {
      this.lastFocus = document.activeElement as HTMLElement;
      this.isOpen = true;
      document.documentElement.style.overflow = 'hidden';
    },
    close() {
      this.isOpen = false;
      document.documentElement.style.overflow = '';
      if (this.lastFocus && typeof this.lastFocus.focus === 'function') {
        this.lastFocus.focus();
      }
    },
  });

  Alpine.plugin(ajax);
  Alpine.plugin(intersect);
  Alpine.plugin(masonry);

  setupFloorplanDownloadForm();
  setupParkingPopup();
});

/**
 * Open the parking-inquiry modal on a click of any `.js-open-parking-popup`
 * element (currently the §12 image-text-cta CTA). Delegated at the document so
 * it works no matter where/when the trigger renders. The trigger is a real
 * <button> (e-button htmlElement:"button"), but preventDefault() keeps it inert
 * if it ever renders as a link.
 */
function setupParkingPopup() {
  document.addEventListener('click', (event) => {
    const trigger = (event.target as HTMLElement | null)?.closest(
      '.js-open-parking-popup',
    );
    if (!trigger) {
      return;
    }
    event.preventDefault();
    (Alpine.store('parkingForm') as { open(): void }).open();
  });
}

/**
 * Wire the gated floorplan-download HubSpot form (front page only; the partial
 * is rendered by app/custom.php on is_front_page()). The inline V4 developer
 * embed (`hs-form-html`) renders once on page load; we pre-fill its hidden
 * fields via the HubspotFormsV4 API and, on successful submit, download the
 * stashed floorplan file and swap to the thank-you step.
 */
function setupFloorplanDownloadForm() {
  const wrapper = document.querySelector<HTMLElement>(
    '.floorplan-download-form[data-hs-form-id]',
  );
  if (!wrapper) {
    return;
  }

  const formId = wrapper.dataset.hsFormId || '';
  const locale = wrapper.dataset.locale || 'sk';
  const store = Alpine.store('floorplanForm') as {
    isOpen: boolean;
    step: 'form' | 'done';
    pendingUrl: string | null;
    formId: string | null;
  };
  store.formId = formId;

  // HubspotFormsV4 is attached to window by the portal-wide embed script.
  /* eslint-disable @typescript-eslint/no-explicit-any */
  const findForm = (): any => {
    const api = (window as any).HubspotFormsV4;
    if (!api || typeof api.getForms !== 'function') {
      return null;
    }
    const forms = api.getForms() || [];
    return (
      forms.find(
        (f: any) =>
          f && typeof f.getFormId === 'function' && f.getFormId() === formId,
      ) || null
    );
  };

  // Field names are namespaced `0-1/<internal_name>` (HubSpot object/field).
  // hbr_building / property_type may be enum-backed → a bad string is silently
  // dropped; each set is wrapped so one rejection can't block the others.
  const prefill = (form: any) => {
    const sets: [string, string][] = [
      ['0-1/download_type', 'floorplan'],
      ['0-1/preferred_language', locale],
      ['0-1/hbr_building', 'apollo_bc_ii'],
      ['0-1/property_type', 'office'],
    ];
    sets.forEach(([name, value]) => {
      try {
        form.setFieldValue(name, value);
      } catch {
        /* field may not exist / rejected the value */
      }
    });
  };

  const whenReady = (cb: (form: any) => void, attempt = 0) => {
    const form = findForm();
    if (form) {
      cb(form);
      return;
    }
    if (attempt > 40) {
      return; // ~10s @ 250ms
    }
    setTimeout(() => whenReady(cb, attempt + 1), 250);
  };

  // The form instance exists once the embed script has rendered the div.
  whenReady(prefill);

  const triggerDownload = () => {
    const url = store.pendingUrl;
    if (!url) {
      return;
    }
    const a = document.createElement('a');
    a.href = url;
    a.download = '';
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  let watchTimer: number | null = null;
  const stopWatch = () => {
    if (watchTimer !== null) {
      clearInterval(watchTimer);
      watchTimer = null;
    }
  };

  const onSubmitted = () => {
    if (store.step === 'done') {
      return; // dedup: the message event and the poll can both fire.
    }
    triggerDownload();
    store.step = 'done';
    stopWatch();
  };

  // Primary detection: the documented HubSpot `message` event, filtered by our
  // form id so the §15 contact form on the same page can't trigger a download.
  window.addEventListener('message', (event: MessageEvent) => {
    const data = event.data;
    if (
      data &&
      data.type === 'hsFormCallback' &&
      data.eventName === 'onFormSubmitted' &&
      data.id === formId &&
      store.isOpen
    ) {
      onSubmitted();
    }
  });

  // Fallback detection: poll getConversionId() while the modal sits on the form
  // step (priestorynivy uses this for the identical embed). onSubmitted() is
  // idempotent, so whichever signal arrives first wins.
  const startWatch = () => {
    stopWatch();
    watchTimer = window.setInterval(() => {
      const form = findForm();
      if (!form || typeof form.getConversionId !== 'function') {
        return;
      }
      Promise.resolve(form.getConversionId())
        .then((id: unknown) => {
          if (id) {
            onSubmitted();
          }
        })
        .catch(() => {});
    }, 400);
  };
  /* eslint-enable @typescript-eslint/no-explicit-any */

  Alpine.effect(() => {
    if (store.isOpen && store.step === 'form') {
      startWatch();
    } else {
      stopWatch();
    }
  });
}
